import type { LocalMessage } from '@swchat/client-sdk';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type DragEvent, type Ref } from 'react';
import { Icon } from '../design';
import { Bubble, type BubbleProps } from './Bubble';
import { dayFmt } from './format';

/** Cách đáy quá ngưỡng này (px) thì coi là đã cuộn lên xem tin cũ → hiện nút cuộn xuống. */
const AWAY_PX = 240;

/** Cuộn sát đầu hơn ngưỡng này (px) thì tải thêm tin cũ. */
const TOP_PX = 120;

/** Tin liền nhau cùng người gửi trong vòng 5 phút → gộp (ẩn avatar/tên). */
const GROUP_WINDOW_MS = 5 * 60_000;

export interface DropHandlers {
  onDragOver?: (e: DragEvent) => void;
  onDragLeave?: () => void;
  onDrop?: (e: DragEvent) => void;
}

/** Vùng tin nhắn: chia theo ngày, mỗi tin là một `Bubble`; kéo-thả file (C1) qua `drop`. */
export function MessageList({
  messages,
  bubble,
  dragOver,
  drop,
  bottomRef,
  onNearBottom,
  hasMore,
  onLoadMore,
}: {
  messages: LocalMessage[];
  /** Props của từng bong bóng (trừ `continued`, do danh sách tính). */
  bubble: (m: LocalMessage) => Omit<BubbleProps, 'continued'>;
  dragOver: boolean;
  drop: DropHandlers;
  bottomRef: Ref<HTMLDivElement>;
  /** Báo khi người dùng cuộn lên xa đáy / quay lại gần đáy (để cha không kéo xuống khi đang đọc tin cũ). */
  onNearBottom?: (near: boolean) => void;
  /** Còn tin cũ hơn chưa tải → hiện nút và tự tải khi cuộn lên sát đầu. */
  hasMore?: boolean;
  onLoadMore?: () => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const [away, setAway] = useState(false);
  /** Vị trí cuộn trước khi tải thêm tin cũ (để giữ nguyên tin đang đọc sau khi chèn lên đầu). */
  const restore = useRef<{ height: number; top: number; first?: string } | undefined>(undefined);
  const loadOlder = useCallback(() => {
    const el = scroller.current;
    if (!el || !hasMore || !onLoadMore || restore.current) return;
    restore.current = { height: el.scrollHeight, top: el.scrollTop, first: messages[0]?.id };
    onLoadMore();
  }, [hasMore, onLoadMore, messages]);
  useLayoutEffect(() => {
    const el = scroller.current;
    const r = restore.current;
    if (!el || !r || messages[0]?.id === r.first) return;
    el.scrollTop = r.top + (el.scrollHeight - r.height);
    restore.current = undefined;
  }, [messages]);
  // Đã hết tin cũ (không thêm được gì) → bỏ chờ để lần cuộn sau không bị chặn.
  useEffect(() => {
    if (!hasMore) restore.current = undefined;
  }, [hasMore]);
  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    if (el.scrollTop < TOP_PX) loadOlder();
    const isAway = el.scrollHeight - el.scrollTop - el.clientHeight > AWAY_PX;
    setAway(isAway);
    onNearBottom?.(!isAway);
  }, [onNearBottom, loadOlder]);
  // Nội dung đổi (tin mới, tải thêm lịch sử) → đo lại.
  useEffect(measure, [measure, messages.length]);

  const grouped = useMemo(() => {
    const out: { day: string; items: LocalMessage[] }[] = [];
    for (const m of messages) {
      const day = dayFmt.format(m.ts);
      if (out.at(-1)?.day !== day) out.push({ day, items: [] });
      out.at(-1)!.items.push(m);
    }
    return out;
  }, [messages]);

  return (
    <div className="ws-messages-wrap">
    {/* Vùng cuộn nhận focus (Tab) để cuộn bằng phím — yêu cầu tiếp cận (W1.6, axe `scrollable-region-focusable`). */}
    <div
      ref={scroller}
      className={`ws-messages${dragOver ? ' drag-over' : ''}`}
      data-testid="messages"
      role="region"
      aria-label="Tin nhắn"
      tabIndex={0}
      onScroll={measure}
      {...drop}
    >
      {hasMore && (
        <button type="button" className="ws-load-older" data-testid="load-older" onClick={loadOlder}>
          Tải thêm tin cũ hơn
        </button>
      )}
      {grouped.map((g) => (
        <div key={g.day} className="ws-day-group">
          <div className="ws-day">
            <span>{g.day}</span>
          </div>
          {g.items.map((m, i) => {
            const prev = g.items[i - 1];
            const continued =
              !!prev && prev.sender_user_id === m.sender_user_id && !prev.deleted && m.ts - prev.ts < GROUP_WINDOW_MS && !m.forwarded_from;
            return <Bubble key={m.id} {...bubble(m)} continued={continued} />;
          })}
        </div>
      ))}
      <div ref={bottomRef} />
    </div>
    {away && (
      <button
        type="button"
        className="ws-jump-bottom"
        aria-label="Cuộn xuống tin mới nhất"
        title="Cuộn xuống tin mới nhất"
        data-testid="jump-bottom"
        onClick={() => scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' })}
      >
        <Icon name="down" size={20} />
      </button>
    )}
    </div>
  );
}
